import { Component, Input, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { SpaContext } from '../Classes/spacontext';
import { GlobalValidator } from '../Common/Classes/globalValidator';
import { TranslateService } from '../Common/Services/translate.service';
import { IdentityServiceV2 } from '../Services/api/v2/identity.service';
import { UsageAgreementsService } from '../Services/api/v2/usageAgreements.service';
import { AuthContextService } from '../Services/authContext.service';
import { SignatureDocuments } from '../Services/Models/Client/SignatureDocuments';
import { SpaEventService } from '../Services/spaEvents.service';

@Component({
	standalone: false,
	selector: 'visiba-document-sign',
	template: /*language=html*/ `
		<div class="overlay" (click)="abort()"></div>
		<div class="container">
			<div class="row containerInner">
				<div class="inner span26 offset25 lspan26 loffset17" *ngIf="formPP !== null">
					<form
						*ngIf="!documents.PrivacyPolicy.HasSigned && currentView === 'PP'"
						class="innerForm"
						novalidate
						(ngSubmit)="onSubmitPP()"
						[formGroup]="formPP"
						[class.ng-submitted]="formPP['submitted']"
					>
						<div class="row center-text">
							<h2>{{ documents.PrivacyPolicy.Title }}</h2>
						</div>
						<div class="row block">
							<div class="row body">
								<div class="offset3 summary" [innerHtml]="documents.PrivacyPolicy.Summary | safeHtml"></div>
							</div>
							<div class="row center-text" [innerHtml]="views.PP.url | safeHtml"></div>
						</div>

						<div *ngIf="documents.PrivacyPolicy.RequiresExplicitAccept" class="row checkboxes">
							<div class="row">
								<visiba-input
									type="checkbox"
									formControlName="pp"
									[staticplaceholder]="'LicenceHolder.Component.DocumentSignComponent.Placeholder.PPAccept' | translate"
								>
								</visiba-input>
							</div>
						</div>

						<div class="row">
							<button class="btn span20 offset3" [class.disabled]="formPP.invalid">
								{{ 'LicenceHolder.Component.DocumentSignComponent.Button.Approve' | translate }}
							</button>
						</div>
						<div class="row abortButton center-text">
							<a (click)="abort()">{{ 'Common.Button.Abort' | translate }}</a>
						</div>
						<div class="row error">
							<div class="span22 offset2 center-text">
								{{ errorMessage }}
							</div>
						</div>
					</form>

					<form
						*ngIf="!documents.TermsOfService.HasSigned && currentView === 'Tos'"
						class="innerForm"
						novalidate
						(ngSubmit)="onSubmitTos()"
						[formGroup]="formTos"
						[class.ng-submitted]="formTos['submitted']"
					>
						<div class="row center-text">
							<h2>{{ documents.TermsOfService.Title }}</h2>
						</div>
						<div class="row block">
							<div class="row body">
								<div class="offset3 summary" [innerHtml]="documents.TermsOfService.Summary | safeHtml"></div>
							</div>
							<div class="row center-text" [innerHtml]="views.Tos.url | safeHtml"></div>
						</div>

						<div *ngIf="documents.TermsOfService.RequiresExplicitAccept" class="row checkboxes">
							<div class="row">
								<visiba-input
									type="checkbox"
									formControlName="tos"
									[staticplaceholder]="'LicenceHolder.Component.DocumentSignComponent.Placeholder.TosAccept' | translate"
								>
								</visiba-input>
							</div>
						</div>

						<div class="row">
							<button class="btn span20 offset3" [class.disabled]="formTos.invalid">
								{{ 'LicenceHolder.Component.DocumentSignComponent.Button.Approve' | translate }}
							</button>
						</div>

						<div class="row abortButton center-text">
							<a (click)="abort()">{{ 'Common.Button.Abort' | translate }}</a>
						</div>
						<div class="row error">
							<div class="span22 offset2 center-text">
								{{ errorMessage }}
							</div>
						</div>
					</form>
				</div>
			</div>
		</div>
	`,
	styleUrls: ['../Css/Components/document-sign.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class DocumentSignComponent {
	@Input('documents') documents: SignatureDocuments;
	public formPP: UntypedFormGroup = null;
	public formTos: UntypedFormGroup = null;
	public errorMessage: string = null;
	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	public views: any = {};
	public currentView: 'PP' | 'Tos' = null;

	constructor(
		private _translateService: TranslateService,
		public _spaContext: SpaContext,
		private _identityService: IdentityServiceV2,
		private _spaEventService: SpaEventService,
		private _builder: UntypedFormBuilder,
		private authContextService: AuthContextService,
		private _usageAgreementsService: UsageAgreementsService,
	) {}

	private getFormControl(key: 'PrivacyPolicy' | 'TermsOfService'): UntypedFormControl {
		const builder = this.documents[key].RequiresExplicitAccept ? Validators.compose([GlobalValidator.requiredCheckbox]) : null;

		return new UntypedFormControl(this.documents.PrivacyPolicy.HasSigned, builder);
	}

	// biome-ignore lint/correctness/noUnusedPrivateClassMembers: suppressed due to migration - should be fixed
	private ngOnInit(): void {
		Object.keys(this.documents).forEach((key) => {
			if (this.documents[key].HasSigned) return;

			const keyShort = key === 'PrivacyPolicy' ? 'PP' : 'Tos';

			this.views[keyShort] = {
				url: this._translateService.get(`LicenceHolder.Component.DocumentSignComponent.Placeholder.${keyShort}`, {
					documentUrl: this.documents[key].Url,
				}),
				signed: false,
			};

			this.currentView = keyShort;
		});

		this.formPP = this._builder.group({
			pp: this.getFormControl('PrivacyPolicy'),
		});

		this.formTos = this._builder.group({
			tos: this.getFormControl('TermsOfService'),
		});
	}
	public onSubmitPP(): void {
		this.formPP['submitted'] = true;

		if (!this.formPP.valid) return;

		this._usageAgreementsService.signDocument('PrivacyPolicy', this.documents.PrivacyPolicy.Language).subscribe(
			() => {
				this.views['PP'].signed = true;
				this.allNeededIsSigned();
			},
			(error) =>
				(this.errorMessage =
					error.errorMessage || this._translateService.get('LicenceHolder.Component.DocumentSignComponent.Error.Default')),
		);
	}

	public onSubmitTos(): void {
		this.formTos['submitted'] = true;

		if (!this.formTos.valid) return;

		this._usageAgreementsService.signDocument('TermsOfService', this.documents.TermsOfService.Language).subscribe(
			() => {
				this.views['Tos'].signed = true;
				this.allNeededIsSigned();
			},
			(error) =>
				(this.errorMessage =
					error.errorMessage || this._translateService.get('LicenceHolder.Component.DocumentSignComponent.Error.Default')),
		);
	}

	private allNeededIsSigned(): void {
		if (Object.keys(this.views).every((key) => this.views[key].signed)) {
			this._spaEventService.onDocumentsSigningCompleted.emit();
		} else {
			this.currentView = this.currentView === 'PP' ? 'Tos' : 'PP';
		}
	}

	public abort(): void {
		this._spaEventService.onDocumentsSigningAborted.emit();
		this._identityService.signOut().subscribe(({ data }) => {
			if (this.authContextService.isOutsider) {
				this._spaEventService.onSignedOutAsOutsider.emit({
					redirectUrl: data.RedirectUrl,
				});
			} else {
				this._spaEventService.onSignedOut.emit({
					redirectUrl: data.RedirectUrl,
				});
			}
		});
	}
}
